import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { Dropdown, Menu, message, Modal, Popover, Select, Spin, Tooltip } from 'antd';
import {
    AppstoreOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    CustomerServiceOutlined,
    DashboardOutlined,
    DeleteOutlined,
    DownOutlined,
    EllipsisOutlined,
    EyeInvisibleOutlined,
    FolderOutlined,
    InboxOutlined,
    FileTextOutlined,
    LogoutOutlined,
    MenuOutlined,
    MoonOutlined,
    NotificationOutlined,
    PlusOutlined,
    SearchOutlined,
    SettingOutlined,
    StarOutlined,
    SunOutlined,
    UserOutlined,
    ClockCircleOutlined,
    BugOutlined,
} from '@ant-design/icons';
import { GiUpgrade } from 'react-icons/gi';

import BlockerIcon from '@assets/icons/blockerIcon.svg?react';
import ChangeRequestIcon from '@assets/icons/changeRequestIcon.svg?react';
import DiscussionIcon from '@assets/icons/discussionIcon.svg?react';
import DoneIcon from '@assets/icons/doneIcon.svg?react';
import FeedbackIcon from '@assets/icons/feedbackIcon.svg?react';
import PmoIcon from '@assets/icons/pmoIcon.svg?react';
import ProjectDetailIcon from '@assets/icons/projectDetailIcon.svg?react';
import ProjectMilestoneIcon from '@assets/icons/projectMilestoneIcon.svg?react';
import QaIcon from '@assets/icons/qaIcon.svg?react';
import SprintIcon from '@assets/icons/sprintIcon.svg?react';
import CollaboratorsIcon from '@assets/icons/collaboratorsIcon.svg?react';
import {
    getModuleIdFromPath,
    getModuleMenuKey,
    MODULE_SIDEBAR_CONFIG,
    useVisibleFeatureItems,
    useVisibleModuleItems,
    useCan,
    useCanCheck,
} from '@/ability';
import { CHAT_SIDEBAR_WIDTH, FEATURE_SIDEBAR_WIDTH_DEFAULT, MAIN_BODY_INSET_DEFAULT_PX } from '@/components/layout/insideShell.styles';
import { SidebarIconSvg } from '@components/ui/SidebarIconSvg';
import { useChatInboxSocket, useClearChatConversation, useCreateChat, useEligibleChatUsers, useLeaveChat, useMarkAllChatsAsRead, useMarkChatAsRead } from '@hooks/chat/useChat';
import { useChatList } from '@hooks/chat/useChatList';
import { resolveChatListItemAvatarUrl } from '@utils/chatAvatar';
import { useChatSocketInit } from '@hooks/chat/useChatSocketInit';
import { projectsListPath } from '@hooks/projects/projectHelpers';
import { filterProjectTabs, useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { useDocTitle } from '@hooks/useDocTitle';
import { useIsClientProfile } from '@hooks/useIsClientProfile';
import { useModuleSidebarSplit } from '@hooks/useModuleSidebarSplit';
import { useNotificationBadge } from '@hooks/useNotificationBadge';
import { useOpenMarketLeadsSidebar } from '@hooks/open-market/useOpenMarketLeads';
import { useAuthStore } from '@stores/auth.store';
import useThemeStore from '@stores/theme.store';
import useUIStore from '@stores/ui.store';
import { getTheme } from '@theme/theme';
import { getAvatarColor } from '@utils/avatarColor';
import { getUserAvatarUrl } from '@utils/userAvatar';
import { isUuid } from '@utils/uuid';
import { ENV } from '@configs/env';

const HORIZONTAL_GESTURE_EPSILON = 0.5;

export function useInsideLayout() {

    const mode = useThemeStore((state) => state.mode);
    const toggleMode = useThemeStore((state) => state.toggleMode);
    const themeColors = getTheme(mode).colors;
    const user = useAuthStore((state) => state.user);
    const currentUserAvatarColor = useMemo(
        () => getAvatarColor(user?.id ?? user?.email ?? ''),
        [user?.id, user?.email],
    );
    const currentUserAvatarUrl = useMemo(
        () => getUserAvatarUrl(user, ENV.API_URL),
        [user?.avatarUrl, user?.avatar, user?.profileImage, user],
    );
    const planKey = useAuthStore((state) => state.planKey);
    const logout = useAuthStore((state) => state.logout);
    const location = useLocation();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [moreModulesOpen, setMoreModulesOpen] = useState(false);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const bodyContainerRef = useRef(null);

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
            if (window.innerWidth <= 1024) {
                useUIStore.getState().setSidebarCollapsed(true);
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (bodyContainerRef.current) {
            bodyContainerRef.current.scrollTop = 0;
            bodyContainerRef.current.scrollLeft = 0;
        }
    }, [location.pathname]);

    /** Same-route tab changes use relative `search` navigation; avoids full-path work and feels snappier. */
    const handleMenuClick = useCallback(
        ({ key }) => {
            try {
                const next = new URL(key, window.location.origin);
                if (next.pathname === location.pathname) {
                    navigate({ search: next.search }, { replace: false });
                } else {
                    navigate(key);
                }
            } catch {
                navigate(key);
            }
            setMoreModulesOpen(false);
            if (isMobile) {
                setDrawerOpen(false);
            }
        },
        [navigate, location.pathname, isMobile],
    );

    const planLabel = (() => {
        if (!planKey) return 'Free';
        if (typeof planKey === 'string') return planKey.charAt(0).toUpperCase() + planKey.slice(1).toLowerCase();
        if (planKey && typeof planKey === 'object' && (planKey.name ?? planKey.key)) return planKey.name ?? planKey.key;
        return 'Free';
    })();

    const workspaces = useMemo(() => {
        const tenants = user?.tenants;
        if (Array.isArray(tenants) && tenants.length > 0) {
            return tenants.map((t) => ({
                id: t.id,
                name: t.name || 'Workspace',
                role: t.roles?.[0] ?? user?.roles?.[0] ?? '',
                planName: planLabel,
            }));
        }
        return [
            {
                id: 'default',
                name: user?.name || 'Workspace',
                role: user?.roles?.[0] ?? '',
                planName: planLabel,
            },
        ];
    }, [user?.tenants, user?.name, user?.roles, planLabel]);

    const defaultTenantId = user?.defaultTenantId;
    const currentWorkspace = useMemo(() => {
        if (defaultTenantId && workspaces.some((w) => w.id === defaultTenantId)) {
            const ws = workspaces.find((w) => w.id === defaultTenantId);
            return { ...ws, planName: planLabel };
        }
        return workspaces.length
            ? { ...workspaces[0], planName: planLabel }
            : { id: 'default', name: 'Workspace', role: '', planName: planLabel };
    }, [workspaces, defaultTenantId, planLabel]);

    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);

    const effectiveWorkspace = useMemo(() => {
        if (selectedWorkspaceId) {
            const ws = workspaces.find((w) => w.id === selectedWorkspaceId);
            if (ws) return { ...ws, planName: planLabel };
        }
        return currentWorkspace;
    }, [selectedWorkspaceId, workspaces, currentWorkspace, planLabel]);

    const handleWorkspaceMenuClick = ({ key }) => {
        if (key === 'add-workspace') {
            return;
        }
        setSelectedWorkspaceId(key);
    };

    const workspaceMenuItems = useMemo(() => {
        const items = workspaces.map((ws) => ({
            key: ws.id,
            label: (
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {effectiveWorkspace.id === ws.id && (
                            <CheckOutlined style={{ color: '#6d28d9', fontSize: 12 }} />
                        )}
                        {effectiveWorkspace.id !== ws.id && <span style={{ width: 12 }} />}
                        <span style={{ fontWeight: 500 }}>{ws.name}</span>
                    </span>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)', marginLeft: 20 }}>
                        {[ws.role, ws.planName].filter(Boolean).join(' · ')}
                    </span>
                </span>
            ),
        }));
        return [
            ...items,
            // { type: 'divider' },
            // {
            //     key: 'add-workspace',
            //     label: (
            //         <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6d28d9' }}>
            //             <PlusOutlined /> Add new workspace
            //         </span>
            //     ),
            // },
        ];
    }, [workspaces, effectiveWorkspace]);

    // Get user initials for avatar
    const getUserInitials = () => {
        if (user?.name) {
            return user.name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
        }
        return user?.email?.[0]?.toUpperCase() || 'U';
    };

    // Get user display name
    const getUserName = () => {
        return user?.name || user?.email?.split('@')[0] || 'User';
    };

    // Get user role (API may send role or roles array e.g. ["Client"])
    const getUserRole = () => {
        return user?.role || (Array.isArray(user?.roles) && user.roles.length ? user.roles[0] : null) || 'User';
    };

    const copyValue = useCallback((value) => {
        if (!value) return;
        navigator?.clipboard?.writeText(value).then(
            () => message.success('Copied to clipboard'),
            () => { },
        );
    }, []);

    // Detect if we're on a tenant route from URL path
    const isTenantRoute = location.pathname.startsWith('/tenant/');

    const role = useAuthStore((state) => state.role);
    const userRole = role || user?.role || null;

    // Module sidebar: visible items by CASL (tenant only; no owner-only modules)
    const visibleModules = useVisibleModuleItems(isTenantRoute, false);
    const { primaryModuleItems, moreModuleItems } = useModuleSidebarSplit(visibleModules);
    const primaryModuleMenuItems = useMemo(() => {
        return primaryModuleItems.map((m) => ({
            key: m.key,
            icon: m.icon ? React.isValidElement(m.icon) ? m.icon : React.createElement(m.icon) : <DashboardOutlined />,
            label: m.label,
        }));
    }, [primaryModuleItems]);

    // Feature sidebar: visible features for current module by CASL (or chat list from API when module is chat)
    const moduleIdForSidebar = getModuleIdFromPath(location.pathname, isTenantRoute);
    const isChatRoute = moduleIdForSidebar === 'chat';
    const [searchParams] = useSearchParams();
    const matchTenantProject = useMatch({ path: '/tenant/projects/:projectId', end: true });
    const matchProject = useMatch({ path: '/projects/:projectId', end: true });
    const matchChatTenant = useMatch({ path: '/tenant/chat/:chatId' });
    const matchChatGlobal = useMatch({ path: '/chat/:chatId' });
    const matchChat = matchChatTenant || matchChatGlobal;
    const chatIdFromPath = matchChat?.params?.chatId;

    /** Project Kanban tabs: allow vertical screen scrolling when needed. */
    const isProjectKanbanLockPageScroll = useMemo(() => {
        return false;
    }, []);

    /** Some project tabs want the scrollbar flush right (no right padding). */
    const isProjectNoRightPaddingTab = useMemo(() => {
        if (!matchTenantProject && !matchProject) return false;
        const tab = (searchParams.get('tab') || '').trim().toLowerCase();
        return tab === 'details' || tab === 'milestones' || tab === 'phases' || tab === 'phase' || tab === 'qa' || tab === 'sprints' || tab === 'tasks' || tab === 'task'
            || tab === 'blockers' || tab === 'pmo' || tab === 'issues-and-upgrades' || tab === 'client-tickets' || tab === 'submission-memos' || tab === 'submissionmemos' || tab === 'submission_memos';
    }, [matchTenantProject, matchProject, searchParams]);

    const isDashboardRoute = location.pathname === '/dashboard' || location.pathname === '/tenant/dashboard';

    /** Main column content insets (px). Adjust per-route here; default 30 on all sides (`MAIN_BODY_INSET_DEFAULT_PX`). */
    const bodyContentPadding = useMemo(() => {
        if (isChatRoute) {
            return { top: 0, right: 0, bottom: 0, left: 0 };
        }
        const inset = {
            top: MAIN_BODY_INSET_DEFAULT_PX,
            right: MAIN_BODY_INSET_DEFAULT_PX,
            bottom: MAIN_BODY_INSET_DEFAULT_PX,
            left: MAIN_BODY_INSET_DEFAULT_PX,
        };
        if (isProjectNoRightPaddingTab) {
            inset.right = 0;
        }
        return inset;
    }, [isChatRoute, isProjectNoRightPaddingTab]);

    const { data: chatListData, isLoading: chatListLoading } = useChatList({
        limit: 50,
        enabled: moduleIdForSidebar === 'chat',
    });
    const chatList = useMemo(() => chatListData?.items ?? [], [chatListData?.items]);

    const [chatSidebarSearch, setChatSidebarSearch] = useState('');
    const [createChatOpen, setCreateChatOpen] = useState(false);
    const [createChatType, setCreateChatType] = useState('dm'); // 'dm' | 'group'
    const [createChatSelectedUserIds, setCreateChatSelectedUserIds] = useState([]);
    const [createChatGroupName, setCreateChatGroupName] = useState('');

    const createChatMutation = useCreateChat();
    const markChatAsReadMutation = useMarkChatAsRead();
    const markAllChatsAsReadMutation = useMarkAllChatsAsRead();
    const clearChatConversationMutation = useClearChatConversation();
    const exitGroupMutation = useLeaveChat();

    const buildChatActionsMenuItems = useCallback(
        (chat) => {
            const type = String(chat?.type || '').toLowerCase();
            const isChannel = chat?.isChannel === true || type === 'channel' || String(chat?.__title || '').startsWith('#');
            const isGroup = type === 'group' || isChannel;
            return [
                {
                    key: 'search',
                    icon: <SearchOutlined />,
                    label: 'Search in conversation',
                },
                { type: 'divider' },
                ...(isGroup
                    ? [
                        {
                            key: 'exit',
                            icon: <LogoutOutlined />,
                            label: 'Exit group',
                        },
                    ]
                    : []),
                {
                    key: 'hide',
                    icon: <EyeInvisibleOutlined />,
                    label: `Hide conversation`,
                },
                {
                    key: 'star',
                    icon: <StarOutlined />,
                    label: 'Star conversation',
                },
                ...(!isGroup
                    ? [
                        { type: 'divider' },
                        {
                            key: 'clear',
                            icon: <DeleteOutlined />,
                            danger: true,
                            label: 'Clear conversation',
                        },
                    ]
                    : []),
            ];
        },
        [],
    );

    const { data: eligibleUsersData, isLoading: eligibleUsersLoading } = useEligibleChatUsers({
        enabled: moduleIdForSidebar === 'chat' && createChatOpen,
    });

    const eligibleUserOptions = useMemo(() => {
        const eligibleUsersByRole = eligibleUsersData?.byRole ?? {};
        const eligibleUsersFlat = eligibleUsersData?.users ?? [];
        const toOption = (u) => {
            const id = u?.id ?? u?.userId ?? u?._id;
            if (!id) return null;
            const name = u?.name ?? u?.fullName ?? u?.email ?? 'User';
            const email = u?.email ?? '';
            return { value: id, label: email ? `${name} (${email})` : name };
        };

        const roleKeys = Object.keys(eligibleUsersByRole || {});
        if (roleKeys.length > 0) {
            return roleKeys
                .sort((a, b) => String(a).localeCompare(String(b)))
                .map((role) => ({
                    label: role,
                    options: (eligibleUsersByRole[role] ?? []).map(toOption).filter(Boolean),
                }))
                .filter((g) => g.options.length > 0);
        }

        return eligibleUsersFlat.map(toOption).filter(Boolean);
    }, [eligibleUsersData]);

    const chatSidebarChats = useMemo(() => {
        const term = (chatSidebarSearch || '').trim().toLowerCase();
        const list = Array.isArray(chatList) ? chatList : [];
        const withTitle = list.map((c) => {
            const participants = c?.participants ?? [];
            const others = participants.filter((p) => String(p.userId ?? p.id) !== String(user?.id));
            const title =
                c?.name ||
                (others.length === 1
                    ? others[0]?.name || others[0]?.email || 'Unknown'
                    : others.map((p) => p?.name || p?.email).filter(Boolean).join(', ')) ||
                'Chat';
            return {
                ...c,
                __title: title,
                __avatarImageUrl: resolveChatListItemAvatarUrl(c, user?.id),
            };
        });
        if (!term) return withTitle;
        return withTitle.filter((c) => String(c.__title || '').toLowerCase().includes(term));
    }, [chatList, chatSidebarSearch, user?.id]);

    const chatSidebarUnreadIds = useMemo(() => {
        return (chatSidebarChats ?? [])
            .filter((c) => (c.unreadCount ?? c.unread ?? 0) > 0)
            .map((c) => c.id ?? c._id)
            .filter(Boolean);
    }, [chatSidebarChats]);

    const chatSidebarGroups = useMemo(() => {
        const directMessages = [];
        const channels = [];
        const projectChannels = [];

        chatSidebarChats.forEach((c) => {
            const hasProject = !!(c.project?.name || c.openMarketLead?.title || c.openMarketLead?.name);
            const type = String(c.type || '').toLowerCase();
            const isDmType = type === 'dm' || type === 'direct';
            const isGroupType = type === 'group';
            const isChannel = c.isChannel === true || type === 'channel' || String(c.__title || '').startsWith('#');
            const participants = c.participants ?? [];
            const isLikelyDm = !isChannel && !hasProject && participants.length > 0 && participants.length <= 2;

            if (hasProject) projectChannels.push(c);
            else if (isDmType || isLikelyDm) directMessages.push(c);
            else if (isChannel || isGroupType) channels.push(c);
            else directMessages.push(c);
        });

        const byTitle = (a, b) => String(a.__title || '').localeCompare(String(b.__title || ''));
        directMessages.sort(byTitle);
        channels.sort(byTitle);
        projectChannels.sort(byTitle);

        return [
            { key: 'direct', label: 'Direct messages', items: directMessages },
            { key: 'channels', label: 'Channels', items: channels },
            { key: 'project', label: 'Project channels', items: projectChannels },
        ];
    }, [chatSidebarChats]);

    const openCreateChat = useCallback((type) => {
        setCreateChatType(type);
        setCreateChatSelectedUserIds([]);
        setCreateChatGroupName('');
        setCreateChatOpen(true);
    }, []);

    const closeCreateChat = useCallback(() => {
        setCreateChatOpen(false);
        setCreateChatSelectedUserIds([]);
        setCreateChatGroupName('');
    }, []);

    const submitCreateChat = useCallback(() => {
        if (createChatType === 'dm' && createChatSelectedUserIds.length !== 1) {
            message.error('Select 1 user for direct message');
            return;
        }
        if (createChatType === 'group' && createChatSelectedUserIds.length < 2) {
            message.error('Select at least 2 users for a group');
            return;
        }
        if (createChatType === 'group' && !String(createChatGroupName || '').trim()) {
            message.error('Enter a group name');
            return;
        }

        createChatMutation.mutate(
            {
                type: createChatType,
                participantIds: createChatSelectedUserIds,
                name: createChatType === 'group' ? createChatGroupName.trim() : undefined,
            },
            {
                onSuccess: (data) => {
                    const chatId = data?.id ?? data?._id ?? data?.data?.id ?? data?.data?._id ?? null;
                    closeCreateChat();
                    if (chatId) {
                        const chatPath = isTenantRoute ? `/tenant/chat/${chatId}` : `/chat/${chatId}`;
                        navigate(chatPath);
                    } else {
                        message.success('Chat created');
                    }
                },
                onError: (err) => {
                    const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to create chat';
                    message.error(msg);
                },
            },
        );
    }, [
        createChatType,
        createChatSelectedUserIds,
        createChatGroupName,
        createChatMutation,
        closeCreateChat,
        isTenantRoute,
        navigate,
    ]);
    const { items: openMarketLeads, isLoading: openMarketLeadsLoading } = useOpenMarketLeadsSidebar({
        enabled: moduleIdForSidebar === 'open-market',
    });
    const openMarketOverviewPath = isTenantRoute ? '/tenant/open-market/overview' : '/open-market/overview';
    const openMarketLeadIdFromUrl = searchParams.get('leadId');

    const openMarketMyLeads = useMemo(() => {
        const uid = user?.id;
        if (!uid || !Array.isArray(openMarketLeads)) return [];
        return openMarketLeads.filter((l) => String(l.clientUserId) === String(uid));
    }, [openMarketLeads, user?.id]);

    const openMarketMyAcceptedBidLeads = useMemo(() => {
        const uid = user?.id;
        if (!uid || !Array.isArray(openMarketLeads)) return [];
        return openMarketLeads.filter((l) =>
            (l.bids || []).some(
                (b) => String(b.submittedById) === String(uid) && (b.status || '').toLowerCase() === 'accepted',
            ),
        );
    }, [openMarketLeads, user?.id]);

    const projectDetailMatch =
        location.pathname.match(/^\/tenant\/projects\/([^/]+)$/) || location.pathname.match(/^\/projects\/([^/]+)$/);
    // `/projects/overview` is a list route, not a project id — only treat real ids as a detail route.
    const projectIdFromPath = isUuid(projectDetailMatch?.[1]) ? projectDetailMatch[1] : null;
    const {
        data: projectDetailData,
        isPending: projectDetailPending,
        isError: projectDetailIsError,
    } = useProjectDetail(projectIdFromPath);
    const projectDetailReady =
        !!projectDetailData &&
        !projectDetailIsError &&
        String(projectDetailData?.id ?? projectDetailData?._id ?? '') === String(projectIdFromPath ?? '');

    const projectShellTitleFromState =
        typeof location.state?.projectShellTitle === 'string' ? location.state.projectShellTitle.trim() : '';

    const projectHeaderDisplayName = useMemo(() => {
        if (projectDetailReady && projectDetailData?.name) return projectDetailData.name;
        if (projectShellTitleFromState) return projectShellTitleFromState;
        if (projectDetailData?.name) return projectDetailData.name;
        return '';
    }, [projectDetailReady, projectDetailData?.name, projectShellTitleFromState]);

    const showProjectBodyLoader =
        !!projectIdFromPath &&
        moduleIdForSidebar === 'projects' &&
        !projectDetailIsError &&
        !projectDetailReady;

    useDocTitle({ location, projectName: projectHeaderDisplayName || projectDetailData?.name });
    const projectTabs = useMemo(
        () => (projectDetailReady ? filterProjectTabs(projectDetailData?.tabs ?? []) : []),
        [projectDetailReady, projectDetailData?.tabs],
    );
    const isClient = useIsClientProfile();

    const { title: featureSidebarTitle, features: visibleFeatures } = useVisibleFeatureItems(
        location.pathname,
        isTenantRoute,
    );
    const canViewAllEmployees = useCan('hr.employees.view_all');
    const canViewAllAttendance = useCan('hr.attendance.view_all');
    const canViewSalary = useCan('hr.salary.view');
    const hasAnyHrViewAll = canViewAllEmployees || canViewAllAttendance || canViewSalary;

    const featureSidebarItems = useMemo(() => {
        if (moduleIdForSidebar === 'hr' && !hasAnyHrViewAll) {
            return [];
        }
        return (visibleFeatures || []).map((f) => ({
            key: f.key,
            icon: f.icon ? React.isValidElement(f.icon) ? f.icon : React.createElement(f.icon) : <DashboardOutlined />,
            label: f.label,
        }));
    }, [visibleFeatures, moduleIdForSidebar, hasAnyHrViewAll]);

    const projectTabDescriptors = useMemo(() => {
        const isCompletionRequests = (id) =>
            id === 'completion-requests' ||
            id === 'completionRequests' ||
            id === 'pmo' ||
            id === 'project-pmo';
        const tabIconComponent = (tabId) => {
            const id = String(tabId ?? '').trim().toLowerCase();
            if (id === 'overview') return ProjectDetailIcon;
            if (id === 'details' || id === 'projectdetail' || id === 'project-details') return ProjectDetailIcon;
            if (id === 'milestones' || id === 'milestone') return ProjectMilestoneIcon;
            if (id === 'phases' || id === 'phase') return ProjectMilestoneIcon;
            if (id === 'sprints' || id === 'sprint') return SprintIcon;
            if (id === 'tasks' || id === 'task') return SprintIcon;
            if (id === 'qa') return QaIcon;
            if (id === 'blockers' || id === 'b') return BlockerIcon;
            if (id === 'discussion' || id === 'discussions') return DiscussionIcon;
            if (id === 'feedback') return FeedbackIcon;
            if (id === 'change-requests' || id === 'changerequests') return ChangeRequestIcon;
            if (id === 'issues-and-upgrades' || id === 'issuesandupgrades' || id === 'issues_and_upgrades') {
                return 'antd:issues-upgrades';
            }
            if (id === 'client-tickets' || id === 'clienttickets' || id === 'client_tickets') {
                return 'antd:client-tickets';
            }
            if (id === 'client' || id === 'clients') return 'antd:client';
            if (id === 'backlogs' || id === 'backlog') return 'antd:backlogs';
            if (isCompletionRequests(id)) return PmoIcon;
            if (id === 'done' || id === 'completed') return DoneIcon;
            if (id === 'settings') return 'antd:settings';
            if (id === 'submission-memos' || id === 'submissionmemos' || id === 'submission_memos') {
                return 'antd:submission-memos';
            }
            if (id === 'collaboration' || id === 'collaborator' || id === 'collaborators') return CollaboratorsIcon;
            if (id === 'time-logs' || id === 'timelogs') {
                return 'antd:time-logs';
            }
            return null;
        };
        return projectTabs.map((tab) => {
            const tabId = String(tab.id ?? '').trim().toLowerCase();
            const TabIcon = tabIconComponent(tab.id);
            const labelText = isCompletionRequests(tab.id) && !isClient ? 'PMO' : String(tab.label ?? '').trim();
            const menuKey = `${location.pathname}?tab=${tab.id}`;
            let tabAbsoluteUrl = menuKey;
            try {
                tabAbsoluteUrl = new URL(menuKey, window.location.origin).href;
            } catch {
                // keep menuKey
            }

            const tabIconEl =
                TabIcon === 'antd:client' ? (
                    <CustomerServiceOutlined />
                ) : TabIcon === 'antd:backlogs' ? (
                    <InboxOutlined />
                ) : TabIcon === 'antd:issues-upgrades' ? (
                    <GiUpgrade size={18} aria-hidden style={{ flexShrink: 0 }} />
                ) : TabIcon === 'antd:client-tickets' ? (
                    <CustomerServiceOutlined />
                ) : TabIcon === 'antd:settings' ? (
                    <SettingOutlined />
                ) : TabIcon === 'antd:submission-memos' ? (
                    <FileTextOutlined style={{ fontSize: 18 }} />
                ) : TabIcon === 'antd:time-logs' ? (
                    <ClockCircleOutlined style={{ fontSize: 18 }} />
                ) : TabIcon ? (
                    <SidebarIconSvg Icon={TabIcon} size={18} className="sidebar-icon-svg" />
                ) : (
                    <FolderOutlined />
                );

            return { key: menuKey, menuKey, labelText, tabId, tabIconEl, tabAbsoluteUrl };
        });
    }, [projectTabs, location.pathname, isClient]);

    const projectSidebarItems = useMemo(
        () =>
            projectTabDescriptors.map((d) => {
                const contextMenuItems = [
                    { key: 'open', label: 'Open' },
                    { key: 'open-new-tab', label: 'Open in new tab' },
                    { type: 'divider' },
                    { key: 'copy-link', label: 'Copy link' },
                ];
                return {
                    key: d.key,
                    label: (
                        <Dropdown
                            trigger={['contextMenu']}
                            menu={{
                                items: contextMenuItems,
                                onClick: ({ key: actionKey, domEvent }) => {
                                    domEvent?.stopPropagation();
                                    domEvent?.preventDefault();
                                    if (actionKey === 'open') {
                                        handleMenuClick({ key: d.menuKey });
                                    } else if (actionKey === 'open-new-tab') {
                                        window.open(d.tabAbsoluteUrl, '_blank', 'noopener,noreferrer');
                                    } else if (actionKey === 'copy-link') {
                                        copyValue(d.tabAbsoluteUrl);
                                    }
                                },
                            }}
                        >
                            <span
                                className={`project-tab-label project-tab-label--${d.tabId}`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    width: '100%',
                                    minWidth: 0,
                                }}
                            >
                                <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0 }}>
                                    {d.tabIconEl}
                                </span>
                                <span style={{ minWidth: 0 }}>{d.labelText || '—'}</span>
                            </span>
                        </Dropdown>
                    ),
                };
            }),
        [projectTabDescriptors, handleMenuClick, copyValue],
    );

    const projectFeatureNavRows = useMemo(
        () =>
            projectTabDescriptors.map((d) => ({
                key: d.key,
                labelText: d.labelText,
                icon: d.tabIconEl,
                tabAbsoluteUrl: d.tabAbsoluteUrl,
            })),
        [projectTabDescriptors],
    );

    /** Ant Design `Menu` items (mobile drawer); mirrors desktop tab list. */
    const projectSidebarDisplayItems = useMemo(() => {
        if (!projectIdFromPath) return [];
        if (!projectDetailReady && !projectDetailIsError) {
            const menuKey = `${location.pathname}?tab=details`;
            return [
                {
                    key: menuKey,
                    label: (
                        <span
                            className="project-tab-label project-tab-label--details project-tab-label--loading-placeholder"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 10,
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0 }}>
                                <SidebarIconSvg Icon={ProjectDetailIcon} size={18} className="sidebar-icon-svg" />
                            </span>
                            <span style={{ minWidth: 0 }}>Overview</span>
                        </span>
                    ),
                },
            ];
        }
        if (projectDetailIsError) return [];
        return projectSidebarItems;
    }, [projectIdFromPath, projectDetailReady, projectDetailIsError, projectSidebarItems, location.pathname]);

    /** Same tabs as `projectSidebarDisplayItems`, shaped for `FeatureSidebarNav` / `FeatureSidebarNavItem` (desktop). */
    const projectFeatureNavDisplayRows = useMemo(() => {
        if (!projectIdFromPath || projectDetailIsError) return [];
        if (!projectDetailReady) {
            const menuKey = `${location.pathname}?tab=details`;
            let tabAbsoluteUrl = menuKey;
            try {
                tabAbsoluteUrl = new URL(menuKey, window.location.origin).href;
            } catch {
                // keep menuKey
            }
            return [
                {
                    key: menuKey,
                    labelText: 'Overview',
                    icon: <SidebarIconSvg Icon={ProjectDetailIcon} size={20} className="sidebar-icon-svg" />,
                    tabAbsoluteUrl,
                },
            ];
        }
        return projectFeatureNavRows;
    }, [projectIdFromPath, projectDetailIsError, projectDetailReady, location.pathname, projectFeatureNavRows]);

    const featureSidebarSelectedKeys = useMemo(() => {
        if (projectIdFromPath) return [location.pathname + (location.search || '')];
        return [location.pathname];
    }, [projectIdFromPath, location.pathname, location.search]);

    const [createBugModalOpen, setCreateBugModalOpen] = useState(false);
    const isCompanyAdmin = (role?.name ?? role) === 'Company Admin' || role === 'owner';
    const canCheck = useCanCheck();
    const canManageBugReports = isCompanyAdmin || canCheck('app.report') || canCheck('app.report.manage') || canCheck('app.reports.manage') || canCheck('app.reports.view') || canCheck('app.bug.view') || canCheck('app.bug.manage');

    // User dropdown menu (onClick handled by handleUserMenuClick)
    const userMenuItems = useMemo(() => {
        const items = [
            { key: '/profile', icon: <UserOutlined />, label: 'Profile' },
            { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },
            { key: 'report-bug', icon: <BugOutlined style={{ color: '#7c3aed' }} />, label: 'Report Bug' },
        ];

        if (canManageBugReports) {
            items.push({ key: '/reports', icon: <NotificationOutlined />, label: 'Bug Reports' });
        }

        items.push({ key: 'logout', icon: <LogoutOutlined />, label: 'Logout', danger: true });
        return items;
    }, [canManageBugReports]);

    // User dropdown menu click (profile/settings/logout) – close dropdown after
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    /** Which surface opened the account menu (`rail` | `feature`) so two `Dropdown`s never both show `open`. */
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [logoutModalOpen, setLogoutModalOpen] = useState(false);
    const handleUserMenuClick = ({ key }) => {
        setUserMenuOpen(false);
        setUserMenuAnchor(null);
        setUserDropdownOpen(false);
        if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
            setDrawerOpen(false);
        }
        if (key === 'report-bug') {
            setCreateBugModalOpen(true);
            return;
        }
        if (key === 'logout') {
            setLogoutModalOpen(true);
            return;
        }
        navigate(isTenantRoute ? `/tenant${key}` : key);
    };
    const handleLogoutConfirm = () => {
        setLogoutModalOpen(false);
        logout();
        navigate('/sign-in');
    };

    // Get selected menu key: feature sidebar uses current path; module sidebar uses active module path
    const selectedKeys = [location.pathname];
    const moduleId = getModuleIdFromPath(location.pathname, isTenantRoute);
    const activeModule = MODULE_SIDEBAR_CONFIG.find((m) => m.id === moduleId);
    const moduleMenuKey = activeModule ? getModuleMenuKey(moduleId, isTenantRoute) : null;
    const isActiveInPrimary = primaryModuleItems.some((m) => m.id === moduleId);
    const primaryModuleSelectedKeys = isActiveInPrimary && moduleMenuKey ? [moduleMenuKey] : [];
    const isMoreModuleActive = moreModuleItems.some((m) => m.id === moduleId);

    const [notificationOpen, setNotificationOpen] = useState(false);
    const { unreadCount, total: notificationsTotal } = useNotificationBadge();
    const notificationsPath = isTenantRoute ? '/tenant/notifications' : '/notifications';
    const isOnNotificationsPage =
        location.pathname === notificationsPath ||
        location.pathname === `${notificationsPath}/unread` ||
        location.pathname === `${notificationsPath}/read`;
    const notificationsReadCount = Math.max(0, (notificationsTotal ?? 0) - (unreadCount ?? 0));
    useChatSocketInit();
    useChatInboxSocket();

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
        const findHorizontalScroller = (startNode) => {
            let el = startNode instanceof HTMLElement ? startNode : null;
            while (el && el !== document.body) {
                if (el.scrollWidth > el.clientWidth + 1) return el;
                el = el.parentElement;
            }
            const fallback = bodyContainerRef.current;
            if (fallback && fallback.scrollWidth > fallback.clientWidth + 1) return fallback;
            return null;
        };
        const onGlobalWheelCapture = (event) => {
            const deltaX = Number(event?.deltaX ?? 0);
            if (Math.abs(deltaX) < HORIZONTAL_GESTURE_EPSILON) return;
            const targetEl = findHorizontalScroller(event.target);
            if (targetEl) {
                targetEl.scrollLeft += deltaX;
            }
            // Force-stop browser history swipe navigation on horizontal gestures.
            event.preventDefault();
            event.stopPropagation();
        };
        window.addEventListener('wheel', onGlobalWheelCapture, { passive: false, capture: true });
        return () => {
            window.removeEventListener('wheel', onGlobalWheelCapture, { capture: true });
        };
    }, []);

    const featureSidebarWidth = isMobile
        ? 0
        : moduleIdForSidebar === 'chat'
            ? CHAT_SIDEBAR_WIDTH
            : (moduleIdForSidebar === 'hr' && !hasAnyHrViewAll)
                ? 0
                : FEATURE_SIDEBAR_WIDTH_DEFAULT;

    const homePath = isTenantRoute ? '/tenant/dashboard' : '/dashboard';

    const breadcrumbItems = useMemo(() => {
        const crumbs = [];
        crumbs.push({ key: 'home', label: 'Home', path: homePath });

        if (projectIdFromPath) {
            const listPath = projectsListPath(location.pathname);
            crumbs.push({ key: 'projects', label: 'Projects', path: listPath });

            const projectNameLabel = projectHeaderDisplayName || 'Project';
            crumbs.push({ key: 'project', label: projectNameLabel, path: null });

            const tabRaw = (searchParams.get('tab') || 'details').trim();
            const tabMatch = projectTabs.find((t) => String(t.id) === tabRaw);
            let tabLabel = 'Overview';
            if (tabMatch?.label) {
                tabLabel = String(tabMatch.label).trim();
            } else if (tabRaw && tabRaw !== 'details') {
                tabLabel = tabRaw.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            }
            crumbs.push({ key: 'project-tab', label: tabLabel, path: null });
            return crumbs;
        }

        const section = String(featureSidebarTitle || activeModule?.label || 'App').replace(/\n/g, ' ');
        crumbs.push({ key: 'section', label: section, path: null });
        let currentLabel = 'Overview';
        const pathKey = location.pathname + (location.search || '');
        const byPath = visibleFeatures.find((f) => f.key === location.pathname || f.key === pathKey);
        if (byPath?.label) currentLabel = String(byPath.label);
        else if (location.pathname === homePath) currentLabel = 'Overview';
        else {
            const seg = location.pathname.split('/').filter(Boolean).pop() || '';
            const isId = /^[0-9a-fA-F]{24}$/.test(seg) || /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}/.test(seg);

            if (isChatRoute && chatIdFromPath) {
                const c = (chatList || []).find((x) => String(x.id || x._id) === String(chatIdFromPath));
                if (c) {
                    const projName = c?.project?.name || c?.openMarketLead?.title || null;
                    const clName =
                        c?.client?.name ||
                        c?.project?.client?.name ||
                        c?.clientName ||
                        (typeof c?.client === 'string' ? c.client : null);

                    if (projName) {
                        currentLabel = projName;
                    } else if (clName) {
                        currentLabel = clName;
                    } else {
                        const type = String(c?.type || '').toLowerCase();
                        const isGroup = type === 'group' || type === 'channel' || c?.isChannel;
                        if (isGroup && c?.name) {
                            currentLabel = c.name;
                        } else {
                            currentLabel = 'Chat';
                        }
                    }
                } else {
                    currentLabel = 'Chat';
                }
            } else {
                currentLabel = seg ? seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Page';
            }
        }
        crumbs.push({ key: 'current', label: currentLabel, path: null });
        return crumbs;
    }, [
        homePath,
        featureSidebarTitle,
        activeModule?.label,
        projectIdFromPath,
        projectHeaderDisplayName,
        projectDetailIsError,
        searchParams,
        projectTabs,
        visibleFeatures,
        location.pathname,
        location.search,
    ]);

    return {
        mode,
        toggleMode,
        themeColors,
        user,
        currentUserAvatarColor,
        currentUserAvatarUrl,
        planLabel,
        logout,
        location,
        navigate,
        drawerOpen,
        setDrawerOpen,
        moreModulesOpen,
        setMoreModulesOpen,
        userDropdownOpen,
        setUserDropdownOpen,
        bodyContainerRef,
        isMobile,
        handleMenuClick,
        workspaces,
        effectiveWorkspace,
        handleWorkspaceMenuClick,
        workspaceMenuItems,
        getUserInitials,
        getUserName,
        getUserRole,
        copyValue,
        isTenantRoute,
        userRole,
        visibleModules,
        primaryModuleItems,
        moreModuleItems,
        primaryModuleMenuItems,
        moduleIdForSidebar,
        isChatRoute,
        searchParams,
        matchTenantProject,
        matchProject,
        isProjectKanbanLockPageScroll,
        isProjectNoRightPaddingTab,
        isDashboardRoute,
        bodyContentPadding,
        chatListData,
        chatListLoading,
        chatList,
        chatSidebarSearch,
        setChatSidebarSearch,
        createChatOpen,
        setCreateChatOpen,
        createChatType,
        setCreateChatType,
        createChatSelectedUserIds,
        setCreateChatSelectedUserIds,
        createChatGroupName,
        setCreateChatGroupName,
        createChatMutation,
        markChatAsReadMutation,
        markAllChatsAsReadMutation,
        clearChatConversationMutation,
        exitGroupMutation,
        buildChatActionsMenuItems,
        eligibleUsersData,
        eligibleUsersLoading,
        eligibleUserOptions,
        chatSidebarChats,
        chatSidebarUnreadIds,
        chatSidebarGroups,
        openCreateChat,
        closeCreateChat,
        submitCreateChat,
        openMarketLeads,
        openMarketLeadsLoading,
        openMarketOverviewPath,
        openMarketLeadIdFromUrl,
        openMarketMyLeads,
        openMarketMyAcceptedBidLeads,
        projectDetailMatch,
        projectIdFromPath,
        projectDetailData,
        projectDetailPending,
        projectDetailIsError,
        projectDetailReady,
        projectHeaderDisplayName,
        showProjectBodyLoader,
        projectTabs,
        isClient,
        featureSidebarTitle,
        visibleFeatures,
        featureSidebarItems,
        projectSidebarDisplayItems,
        projectFeatureNavDisplayRows,
        featureSidebarSelectedKeys,
        userMenuItems,
        createBugModalOpen,
        setCreateBugModalOpen,
        userMenuOpen,
        setUserMenuOpen,
        userMenuAnchor,
        setUserMenuAnchor,
        logoutModalOpen,
        setLogoutModalOpen,
        handleUserMenuClick,
        handleLogoutConfirm,
        selectedKeys,
        moduleId,
        activeModule,
        moduleMenuKey,
        isActiveInPrimary,
        primaryModuleSelectedKeys,
        isMoreModuleActive,
        notificationOpen,
        setNotificationOpen,
        unreadCount,
        notificationsTotal,
        notificationsPath,
        isOnNotificationsPage,
        notificationsReadCount,
        featureSidebarWidth,
        breadcrumbItems,
        homePath,
    };
}
